import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/common/SEO';
import FAQSection from '../components/common/FAQSection';
import { FaAws, FaWindows } from 'react-icons/fa';
import {
  SiDatabricks,
  SiSnowflake,
  SiGooglebigquery,
  SiCloudera,
} from 'react-icons/si';

function MetricAgent() {
  const supportedPlatforms = [
    { name: 'Databricks', icon: <SiDatabricks size={18} color="#FF3621" /> },
    { name: 'Snowflake', icon: <SiSnowflake size={18} color="#29B5E8" /> },
    { name: 'Redshift', icon: <FaAws size={18} color="#FF9900" /> },
    { name: 'BigQuery', icon: <SiGooglebigquery size={18} color="#4285F4" /> },
    { name: 'Fabric', icon: <FaWindows size={18} color="#00A4EF" /> },
    { name: 'Cloudera', icon: <SiCloudera size={18} color="#F96702" /> },
  ];

  const supportedFrameworks = ['Python', 'SQL', 'dbt', 'DLT', 'Snowpark'];

  const goldModelingCapabilities = [
    {
      title: 'Pattern Query Interrogation',
      desc: 'Uses warehouse history and query-shape analysis to identify repeated GROUP BY paths, expensive joins, and the exact aggregations worth precomputing.',
      accent: 'linear-gradient(135deg, #0EA5E9 0%, #2563EB 100%)',
    },
    {
      title: 'Metric Type Awareness',
      desc: 'Distinguishes additive, semi-additive, and non-additive metrics so balances, ratios, snapshots, and financial measures are modeled correctly from the start.',
      accent: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
    },
    {
      title: 'Materialization Decisions',
      desc: 'Chooses between Gold tables, Materialized Views, and Dynamic Tables based on freshness SLA, cost profile, query pressure, and refresh complexity.',
      accent: 'linear-gradient(135deg, #14B8A6 0%, #0F766E 100%)',
    },
    {
      title: 'Deterministic Compilation',
      desc: 'Turns a structured metric spec into validated artifacts through a compiler flow, reducing guesswork and keeping semantic logic consistent across environments.',
      accent: 'linear-gradient(135deg, #F59E0B 0%, #EA580C 100%)',
    },
    {
      title: 'Dimension Governance',
      desc: 'Normalizes dimensions, validates compatibility across measures, and blocks broken combinations before inconsistent business definitions leak downstream.',
      accent: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
    },
  ];

  return (
    <>
      <SEO pathname="/agents/metric-modeler" agentKey="MetricAgent" />
      <main className="agent-page" style={{ paddingTop: '80px', minHeight: '100vh', background: 'var(--color-bg-light)', color: 'var(--color-text-primary)' }}>
        <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px 20px 0' }}>
          <Link to="/" style={{ color: '#0071e3', textDecoration: 'none', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '0.95rem' }}>
            <span style={{ fontSize: '1.2rem' }}>←</span> Back to Home
        </Link>
      </div>

        {/* Hero Section */}
        <section style={{ padding: '100px 0 120px', background: '#020617', position: 'relative', overflow: 'hidden', color: 'white', fontFamily: '"General Sans", sans-serif' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 18% 18%, rgba(14,165,233,0.22) 0%, rgba(14,165,233,0) 34%), radial-gradient(circle at 82% 24%, rgba(20,184,166,0.2) 0%, rgba(20,184,166,0) 30%), linear-gradient(180deg, rgba(2,6,23,0.9) 0%, rgba(2,6,23,0.98) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, backgroundImage: 'url(/images/agents/metric-agent-pipelines.png)', backgroundPosition: 'center right', backgroundSize: 'cover', opacity: 0.12, filter: 'saturate(0.9) contrast(1.05)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(2,6,23,0.96) 0%, rgba(2,6,23,0.9) 42%, rgba(2,6,23,0.72) 100%)' }} />

          <div className="container" style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', position: 'relative', zIndex: 2 }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '64px', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: '1.2 1 0%', minWidth: '320px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', padding: '6px 16px', background: 'rgba(255,255,255,0.08)', color: 'white', border: '1px solid rgba(255,255,255,0.16)', borderRadius: '30px', fontSize: '0.85rem', fontWeight: 700, marginBottom: '24px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                  <span style={{ display: 'inline-block', width: '6px', height: '6px', background: '#2dd4bf', borderRadius: '50%', marginRight: '8px', boxShadow: '#2dd4bf 0 0 10px' }} />
                  Principal Analytics Engineering Agent
              </div>
                <h1 style={{ fontSize: 'clamp(3rem, 5vw, 4.2rem)', marginBottom: '24px', color: 'white', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-0.04em', maxWidth: '760px' }}>
                  Build <span style={{ color: '#67e8f9' }}>semantic models</span> that survive real warehouse pressure.
              </h1>
                <p style={{ fontSize: '1.22rem', color: '#cbd5e1', marginBottom: '20px', lineHeight: 1.72, maxWidth: '680px' }}>
                  The Metric Agent acts like a principal analytics engineer for your platform. It designs Gold models, semantic layers, and physical metric pipelines with shift-left governance, FinOps discipline, and warehouse-native performance decisions built in from the first pass.
              </p>
                <p style={{ fontSize: '1.04rem', color: '#94a3b8', marginBottom: '36px', lineHeight: 1.78, maxWidth: '700px' }}>
                  Instead of guessing from static requirements, it reads actual query behavior, respects grain and dimensional compatibility, evaluates freshness SLAs, and compiles the right outcome for Databricks, Snowflake, Redshift, BigQuery, Fabric, or Cloudera using Python, SQL, dbt, DLT, and Snowpark.
              </p>
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '26px' }}>
                  <a href="https://app.eagentix.com/register?reason=Requesting%20access%20to%20Metric%20Agent" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#67e8f9', color: '#082f49', padding: '16px 32px', borderRadius: '12px', fontWeight: 700, fontSize: '1.05rem', textDecoration: 'none', boxShadow: 'rgba(103,232,249,0.28) 0 10px 28px -12px' }}>
                    Request Access Now
                </a>
              </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {['Shift-Left Governance', 'FinOps Materialization', 'Spec-First Compilation', 'Semantic Publishing'].map((item) => (
                    <span key={item} style={{ padding: '10px 14px', borderRadius: '999px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: '#e2e8f0', fontSize: '0.84rem', fontWeight: 600, letterSpacing: '0.01em' }}>
                      {item}
                  </span>
                  ))}
              </div>
            </div>

              <div style={{ flex: '1 1 0%', minWidth: '320px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
                <div style={{ background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.1)', padding: '32px', borderRadius: '24px', boxShadow: '0 28px 50px -36px rgba(15,23,42,0.8)' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'rgba(255,255,255,0.46)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '24px', textAlign: 'center' }}>
                    Supported Platforms
                </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px' }}>
                    {supportedPlatforms.map((item) => (
                      <div key={item.name} style={{ padding: '12px 14px', borderRadius: '16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0', fontSize: '0.92rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '10px' }}>
                        {item.icon}
                        <span>{item.name}</span>
                    </div>
                    ))}
                </div>
              </div>

                <div style={{ background: 'rgba(255,255,255,0.04)', backdropFilter: 'blur(14px)', border: '1px solid rgba(255,255,255,0.1)', padding: '32px', borderRadius: '24px', boxShadow: '0 28px 50px -36px rgba(15,23,42,0.8)' }}>
                  <p style={{ fontSize: '0.75rem', fontWeight: 800, color: 'rgba(255,255,255,0.46)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '18px', textAlign: 'center' }}>
                    Compiler Outputs
                </p>
                  <p style={{ margin: '0 0 20px', color: '#cbd5e1', lineHeight: 1.7, fontSize: '0.98rem', textAlign: 'center' }}>
                    From semantic intent to physical delivery: the agent decides between Gold tables, materialized views, dynamic tables, and semantic publishing paths without losing governance metadata or refresh intent.
                </p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '18px' }}>
                    {supportedFrameworks.map((item) => (
                      <span key={item} style={{ padding: '9px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)', color: '#e2e8f0', fontSize: '0.8rem', fontWeight: 700 }}>
                        {item}
                    </span>
                    ))}
                </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
                    {['Gold Tables', 'Materialized Views', 'Dynamic Tables', 'Semantic Models'].map((item) => (
                      <div key={item} style={{ padding: '12px 14px', borderRadius: '16px', background: 'linear-gradient(135deg, rgba(103,232,249,0.1), rgba(45,212,191,0.04))', border: '1px solid rgba(103,232,249,0.12)', color: '#ecfeff', fontSize: '0.84rem', fontWeight: 700, textAlign: 'center' }}>
                        {item}
                    </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

        {/* Platform Showcase */}
        <section style={{ padding: '72px 0', background: 'white', borderTop: '1px solid rgba(0,0,0,0.05)', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
          <div className="container">
            <div style={{ textAlign: 'center', marginBottom: '48px', maxWidth: '820px', marginLeft: 'auto', marginRight: 'auto' }}>
              <span className="section-label">Platform-Aware Metric Pipelines</span>
              <h2 style={{ fontSize: '2.5rem', color: '#1d1d1f', fontWeight: '800', letterSpacing: '-0.03em', margin: '16px 0' }}>
                Databricks and Snowflake pipeline strategies, modeled visually
            </h2>
              <p style={{ fontSize: '1.1rem', color: 'var(--color-text-secondary)', lineHeight: '1.7', margin: 0 }}>
                The agent does not stop at metric names. It reasons through medallion flows, query-pattern-driven pre-aggregation, semantic publishing, freshness SLAs, shift-left governance, and FinOps-aware decisions about whether the right answer is a Gold table, a Materialized View, or a Dynamic Table.
            </p>
          </div>

            <div style={{ borderRadius: '28px', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 30px 60px -32px rgba(15,23,42,0.22)' }}>
              <img
                src="/images/agents/metric-agent-pipelines.png"
                alt="Metric Agent visual showing Databricks and Snowflake semantic pipeline strategies"
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
          </div>
        </div>
      </section>

        {/* Advanced Capabilities Section */}
        <section style={{ padding: '96px 0', background: 'radial-gradient(circle at top, rgba(153,246,228,0.18) 0%, rgba(255,255,255,0.96) 22%, #f8fafc 58%, #ffffff 100%)', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
          <div className="container">
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 0.95fr) minmax(0, 1.25fr)', gap: '28px', alignItems: 'start' }}>
              <div style={{ position: 'sticky', top: '110px' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: '8px 14px', borderRadius: '999px', background: 'rgba(255,255,255,0.84)', border: '1px solid rgba(148,163,184,0.22)', boxShadow: '0 16px 30px -22px rgba(15,23,42,0.18)' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: '#14B8A6', boxShadow: '0 0 18px rgba(20,184,166,0.55)' }} />
                  <span className="section-label" style={{ margin: 0 }}>Agent Capabilities</span>
              </div>
                <h2 style={{ fontSize: 'clamp(2.7rem, 5vw, 4.8rem)', color: '#0f172a', fontWeight: '900', letterSpacing: '-0.05em', lineHeight: '0.94', margin: '24px 0 18px' }}>
                  Sophisticated <span style={{ color: '#0F766E' }}>Gold</span> Modeling
              </h2>
                <p style={{ fontSize: '1.12rem', color: '#475569', lineHeight: '1.82', marginBottom: '24px', maxWidth: '560px' }}>
                  This is not just metric naming. The agent reasons through grain, query patterns, freshness requirements, semantic consistency, shift-left guardrails, and FinOps constraints, then translates those decisions into production-ready Gold-layer structures.
              </p>
                <div style={{ borderRadius: '28px', padding: '28px', background: 'linear-gradient(145deg, #0f172a 0%, #134e4a 100%)', color: 'white', boxShadow: '0 34px 64px -30px rgba(15,23,42,0.38)' }}>
                  <p style={{ margin: 0, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.16em', color: '#99F6E4', fontWeight: '800' }}>Semantic Design Engine</p>
                  <p style={{ margin: '14px 0 0', color: '#CCFBF1', lineHeight: '1.8', fontSize: '1.02rem' }}>
                    It discovers how analysts actually query data, chooses the right physical strategy, and enforces business-safe metric definitions before anything reaches dashboards or executive reports, keeping cost and refresh posture under control from day one.
                </p>
              </div>
            </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '22px' }}>
                <div style={{ gridColumn: '1 / -1', borderRadius: '28px', padding: '30px', background: 'linear-gradient(145deg, rgba(255,255,255,0.96) 0%, rgba(240,253,250,0.98) 100%)', border: '1px solid rgba(20,184,166,0.12)', boxShadow: '0 24px 44px -30px rgba(15,23,42,0.18)', position: 'relative', overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', top: '-40px', right: '-20px', width: '180px', height: '180px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(20,184,166,0.18) 0%, rgba(20,184,166,0) 70%)' }} />
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ width: '58px', height: '58px', borderRadius: '18px', background: 'linear-gradient(135deg, #14B8A6 0%, #0EA5E9 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.45rem', fontWeight: '800', boxShadow: '0 18px 34px -18px rgba(20,184,166,0.55)' }}>
                      G
                  </div>
                    <h3 style={{ fontSize: '1.55rem', margin: '18px 0 10px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.03em' }}>
                      From Silver behavior to Gold strategy
                  </h3>
                    <p style={{ color: '#475569', lineHeight: '1.8', fontSize: '1rem', margin: 0 }}>
                      The strongest outcome is not a prettier SQL file. It is a semantic layer designed around actual warehouse behavior, governed dimensions, correct metric math, and cost-aware materialization choices.
                  </p>
                </div>
              </div>

                {goldModelingCapabilities.map((capability) => (
                  <div key={capability.title} style={{ borderRadius: '24px', padding: '26px', background: 'rgba(255,255,255,0.82)', backdropFilter: 'blur(14px)', border: '1px solid rgba(148,163,184,0.14)', boxShadow: '0 20px 36px -30px rgba(15,23,42,0.18)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', inset: 0, background: capability.accent, opacity: 0.08, filter: 'blur(72px)', transform: 'translate(30%, -36%)' }} />
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '16px', background: capability.accent, boxShadow: '0 14px 28px -18px rgba(15,23,42,0.28)', marginBottom: '16px' }} />
                      <h3 style={{ fontSize: '1.18rem', marginBottom: '10px', color: '#0f172a', fontWeight: '800', letterSpacing: '-0.02em', lineHeight: '1.2' }}>
                        {capability.title}
                    </h3>
                      <p style={{ color: '#475569', lineHeight: '1.75', fontSize: '0.97rem', margin: 0 }}>
                        {capability.desc}
                    </p>
                  </div>
                </div>
                ))}
            </div>
          </div>
        </div>
      </section>
      <FAQSection agentKey="MetricAgent" />
      </main>
    </>
  );
}

export default MetricAgent;
